import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaRobot, FaHeartbeat, FaMicrophone, FaChartLine, FaNewspaper, FaCloud, FaBrain } from 'react-icons/fa';
import './Projects.css';

const projects = [
  {
    title: 'Auto-RAG Chatbot for KeepMe Support',
    category: 'AI Agent',
    icon: <FaRobot />,
    description:
      'Built a RAG chatbot using the Auto-RAG pattern with hybrid search and re-ranker design to improve accuracy. Used the Ragas framework to evaluate the chatbot, achieving 95% accuracy.',
    tech: ['LangGraph', 'Pinecone', 'Ragas', 'Hybrid Search', 'Re-ranker'],
    highlight: '95% Accuracy',
  },
  {
    title: 'End-to-End MLOps Pipeline for Loan Default Prediction',
    category: 'ML',
    icon: <FaChartLine />,
    description:
      'Built a production-ready MLOps pipeline automating the ML lifecycle. Orchestrated training with Airflow and MLflow. Deployed FastAPI inference API with hot-reloading and integrated Evidently AI for drift monitoring.',
    tech: ['Airflow', 'MLflow', 'FastAPI', 'Docker', 'Evidently AI', 'Redis'],
    highlight: 'MLOps Pipeline',
  },
  {
    title: 'Health Assistant AI Agent',
    category: 'AI Agent',
    icon: <FaHeartbeat />,
    description:
      'AI-powered health assistant with appointment booking, rescheduling, and cancellation workflows. Integrated LangSmith for observability and MCP servers for seamless system access.',
    tech: ['LangGraph', 'FastAPI', 'PostgreSQL', 'Docker', 'LangSmith', 'MCP'],
    highlight: 'MCP Integration',
  },
  {
    title: 'AI Voice Agent',
    category: 'AI Agent',
    icon: <FaMicrophone />,
    description:
      'AI-powered voice assistant for real-time spoken interactions. Implemented STT with OpenAI, TTS using ElevenLabs, VAD with Silero, and conversational understanding with GPT-4o.',
    tech: ['LiveKit', 'OpenAI', 'ElevenLabs', 'Silero VAD', 'GPT-4o'],
    highlight: 'Real-time Voice',
  },
  {
    title: 'Heart Risk Analysis Application',
    category: 'ML',
    icon: <FaHeartbeat />,
    description:
      'ML model to predict heart disease risk using ensemble techniques. Applied cross-validation and hyperparameter tuning with GridSearchCV. Built with Streamlit + FastAPI.',
    tech: ['scikit-learn', 'GridSearchCV', 'Streamlit', 'FastAPI', 'Docker', 'Render'],
    highlight: 'Deployed on Render',
  },
  {
    title: 'Chat With Latest News (SaaS)',
    category: 'RAG',
    icon: <FaNewspaper />,
    description:
      'Microservice for a SaaS platform to crawl, schedule, and store news articles in PostgreSQL with pgvector. RAG-based chatbot for dynamic, multi-turn conversations.',
    tech: ['FastAPI', 'PostgreSQL', 'pgvector', 'RAG', 'Microservice'],
    highlight: 'SaaS Platform',
  },
  {
    title: 'LLM Fine-tuning with LoRA',
    category: 'ML',
    icon: <FaBrain />,
    description:
      'Fine-tuned Llama-3.2-3B-Instruct on ServiceNow R1-Distill-SFT dataset (171K examples) using Unsloth with LoRA for parameter-efficient training.',
    tech: ['Llama-3.2', 'LoRA', 'Unsloth', 'ServiceNow Dataset'],
    highlight: '171K Examples',
  },
  {
    title: 'Credit Score Prediction Model',
    category: 'ML',
    icon: <FaChartLine />,
    description:
      'Deep learning classification model for credit score prediction. Built with Streamlit + FastAPI, deployed with Docker, tracked with MLflow.',
    tech: ['Deep Learning', 'Streamlit', 'FastAPI', 'Docker', 'MLflow'],
    highlight: 'MLflow Tracking',
  },
  {
    title: 'ETL Pipeline for Weather Data',
    category: 'Data',
    icon: <FaCloud />,
    description:
      'ETL pipeline to process weather data using Python and Pandas. Used Airflow to schedule API extraction and loaded data into AWS S3.',
    tech: ['Python', 'Pandas', 'Airflow', 'AWS S3', 'ETL'],
    highlight: 'Airflow Scheduled',
  },
  {
    title: 'Post Analysis Model',
    category: 'ML',
    icon: <FaChartLine />,
    description:
      'Sentiment analysis model to classify X App posts as positive or negative using spaCy. Fetched data through X API with feature engineering using spaCy and NLTK.',
    tech: ['spaCy', 'NLTK', 'X API', 'NLP', 'Sentiment Analysis'],
    highlight: 'NLP Pipeline',
  },
  {
    title: 'People Finance Report Dashboard',
    category: 'Data',
    icon: <FaChartLine />,
    description:
      'Power BI dashboard analyzing financial behavior of US individuals. Interactive visualizations with filters, drill-downs, and DAX calculations.',
    tech: ['Power BI', 'DAX', 'Data Visualization', 'Financial Analysis'],
    highlight: 'Interactive BI',
  },
];

const categories = ['All', 'AI Agent', 'RAG', 'ML', 'Data'];

const Projects = () => {
  const [active, setActive] = useState('All');

  const filtered = active === 'All' ? projects : projects.filter((p) => p.category === active);

  return (
    <section id="projects" className="projects">
      <div className="container">
        <div className="section-header">
          <span className="section-label">{'// Portfolio'}</span>
          <h2 className="section-title">Featured Projects</h2>
          <p className="section-subtitle">
            A collection of AI agents, ML models, and data engineering solutions
          </p>
        </div>

        <div className="projects__filters">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`projects__filter ${active === cat ? 'projects__filter--active' : ''}`}
              onClick={() => setActive(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="projects__grid">
            {filtered.map((project) => (
              <div
                className="projects__card"
                key={project.title}
              >
                <div className="projects__card-top">
                  <div className="projects__card-icon">{project.icon}</div>
                  <span className="projects__card-category">{project.category}</span>
                </div>
                <h3 className="projects__card-title">{project.title}</h3>
                <p className="projects__card-desc">{project.description}</p>
                {project.highlight && (
                  <div className="projects__card-highlight">
                    <span>{project.highlight}</span>
                  </div>
                )}
                <div className="projects__card-tech">
                  {project.tech.map((t) => (
                    <span key={t} className="tag">{t}</span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
